import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * 2026-10-15 (v0.17 Stufe 3): der Ablauf einer Kartenzahlung am Kiosk -- starten, nachfragen,
 * abbrechen -- für den Sammelgast-Dialog (Stufe 2) und die Zahlung einer Person (Stufe 3).
 *
 * Herausgelöst, damit es die Nachfrage-Logik nur EINMAL gibt: Sie hat am 15.10. schon einmal
 * still versagt (der Zeitgeber startete bei jedem Neuzeichnen des Kiosks von vorn), und zwei Kopien
 * hätten zwei Gelegenheiten dafür. Der Dialog verbucht nichts selbst -- er zeigt, was der SERVER
 * nach dem Nachschlagen bei SumUp sagt (ADR-0009 §1).
 */

export type KartenStatusWert = 'PENDING' | 'SUCCESSFUL' | 'UNASSIGNED' | 'FAILED' | 'EXPIRED';

export interface Kartenzahlung {
  cardPaymentId: string;
  status: KartenStatusWert;
  amountCents: number;
  grund?: string;
  balanceCents?: number;
}

/** Wie oft nachgefragt wird -- der Server fragt SumUp höchstens alle zwei Sekunden. */
export const NACHFRAGE_MS = 2_000;
/** So lange steht „Zahlung erhalten", bevor der Dialog sich selbst schließt (FR-257). */
export const ERFOLG_MS = 2_500;

export type KartenPhase =
  | { art: 'bereit' }
  | { art: 'warten'; zahlung: Kartenzahlung; abgebrochen: boolean }
  | { art: 'erfolg'; zahlung: Kartenzahlung; dochErhalten: boolean }
  | { art: 'keineAntwort'; zahlung: Kartenzahlung }
  | { art: 'gescheitert'; grund: string }
  | { art: 'ohneZuordnung'; zahlung: Kartenzahlung };

/** Ein Event-Schluss sagt der Server auf Englisch; jede andere 409 ist ein Satz für den Bediener. */
export function eventGeschlossen(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && /closed/i.test(e.message);
}

function grundText(z: Kartenzahlung): string {
  if (z.grund === 'abgelehnt') return 'Die Karte wurde abgelehnt.';
  if (z.grund === 'abgebrochen') return 'Die Zahlung wurde am Terminal abgebrochen.';
  if (z.grund) return `Die Zahlung ist nicht zustande gekommen (${z.grund}).`;
  return 'Die Zahlung ist nicht zustande gekommen.';
}

/** Was aus einem Start wurde, soweit der Aufrufer darauf reagieren muss. */
export type StartErgebnis =
  | { art: 'gestartet' }
  /** 412: Der gezeigte Betrag stimmt nicht mehr -- neu laden, nichts kassieren. */
  | { art: 'geaendert' }
  /** Ein Satz für den Bediener („Terminal gerade belegt" …). */
  | { art: 'fehler'; text: string }
  /** Bereits behandelt (Event geschlossen, Gerät abgemeldet). */
  | { art: 'erledigt' };

export function useKartenzahlung({
  onAuthError,
  onErfolg,
}: {
  /** `true`, wenn der Fehler behandelt ist (Event geschlossen, Gerät abgemeldet). */
  onAuthError: (e: unknown) => boolean;
  /** Nach „Zahlung erhalten" und `ERFOLG_MS` -- mit dem neuen offenen Saldo. */
  onErfolg: (balanceCents: number) => void;
}) {
  const [phase, setPhase] = useState<KartenPhase>({ art: 'bereit' });

  // Über eine Ref, nicht als Abhängigkeit: Der Kiosk erzeugt Rückrufe bei jedem Zeichnen neu, und
  // er zeichnet nach einer Buchung zwei Minuten lang jede Sekunde (siehe oben).
  const rueckrufe = useRef({ onAuthError, onErfolg });
  useEffect(() => {
    rueckrufe.current = { onAuthError, onErfolg };
  });

  const uebernimm = useCallback((z: Kartenzahlung, abgebrochen: boolean) => {
    switch (z.status) {
      case 'PENDING':
        setPhase({ art: 'warten', zahlung: z, abgebrochen });
        break;
      case 'SUCCESSFUL':
        setPhase({ art: 'erfolg', zahlung: z, dochErhalten: abgebrochen });
        break;
      case 'UNASSIGNED':
        setPhase({ art: 'ohneZuordnung', zahlung: z });
        break;
      case 'FAILED':
        setPhase({ art: 'gescheitert', grund: grundText(z) });
        break;
      case 'EXPIRED':
        // Nach „Abbrechen" ist das das erwartete Ende; ohne Abbruch heißt es: keine Antwort.
        setPhase(
          abgebrochen
            ? { art: 'gescheitert', grund: 'Die Zahlung wurde abgebrochen.' }
            : { art: 'keineAntwort', zahlung: z },
        );
        break;
    }
  }, []);

  // Solange gewartet wird: alle zwei Sekunden nachfragen. Kettet sich selbst, damit sich langsame
  // Antworten nicht überholen. Weitergeschaltet wird über `runde` -- die Abhängigkeiten sind genau
  // das, was eine neue Nachfrage auslösen SOLL, und nichts, was der Kiosk beim Zeichnen erneuert.
  const wartendeId = phase.art === 'warten' ? phase.zahlung.cardPaymentId : undefined;
  const wartetAbgebrochen = phase.art === 'warten' && phase.abgebrochen;
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    if (!wartendeId) return;
    const timer = setTimeout(async () => {
      try {
        const z = await api.get<Kartenzahlung>(`/kiosk/card-payments/${wartendeId}`, 'kiosk');
        uebernimm(z, wartetAbgebrochen);
      } catch (e) {
        if (rueckrufe.current.onAuthError(e)) return;
        // Ein Aussetzer im Netz ist kein Ergebnis -- weiter nachfragen.
      }
      setRunde((r) => r + 1);
    }, NACHFRAGE_MS);
    return () => clearTimeout(timer);
  }, [wartendeId, wartetAbgebrochen, runde, uebernimm]);

  // Erfolg: kurz zeigen, dann geht es weiter (FR-257).
  const erfolg = phase.art === 'erfolg' ? phase.zahlung : undefined;
  useEffect(() => {
    if (!erfolg) return;
    const timer = setTimeout(() => rueckrufe.current.onErfolg(erfolg.balanceCents ?? 0), ERFOLG_MS);
    return () => clearTimeout(timer);
  }, [erfolg]);

  const starte = useCallback(
    async (body: Record<string, unknown>): Promise<StartErgebnis> => {
      try {
        const z = await api.post<Kartenzahlung>('/kiosk/card-payments', body, 'kiosk');
        uebernimm(z, false);
        return { art: 'gestartet' };
      } catch (e) {
        setPhase({ art: 'bereit' });
        if (e instanceof ApiError && e.status === 412) return { art: 'geaendert' };
        // Eine 403 MIT eigenem Satz ist fachlich („am Kiosk ausgeblendet") -- der Autorisierer
        // antwortet ohne `error` (Lehre vom 02.10.: jede 403 als „abgemeldet" zu lesen war falsch).
        const fachlich403 =
          e instanceof ApiError &&
          e.status === 403 &&
          typeof e.body === 'object' &&
          e.body !== null &&
          'error' in e.body;
        if (
          e instanceof ApiError &&
          (((e.status === 409 || e.status === 502 || e.status === 400) && !eventGeschlossen(e)) ||
            fachlich403)
        ) {
          // „Terminal gerade belegt", „nicht angenommen" -- der Satz des Servers.
          return { art: 'fehler', text: e.message };
        }
        if (rueckrufe.current.onAuthError(e)) return { art: 'erledigt' };
        return {
          art: 'fehler',
          text: 'Die Kartenzahlung konnte nicht gestartet werden. Bitte erneut versuchen.',
        };
      }
    },
    [uebernimm],
  );

  // „Abbrechen" ist nur eine Bitte an das Terminal -- danach wird weiter gefragt (AC-241).
  const abbrechen = useCallback(
    async (z: Kartenzahlung) => {
      setPhase({ art: 'warten', zahlung: z, abgebrochen: true });
      try {
        const jetzt = await api.post<Kartenzahlung>(
          `/kiosk/card-payments/${z.cardPaymentId}/cancel`,
          {},
          'kiosk',
        );
        uebernimm(jetzt, true);
      } catch (e) {
        rueckrufe.current.onAuthError(e);
      }
    },
    [uebernimm],
  );

  const erneutNachfragen = useCallback(
    (z: Kartenzahlung) => setPhase({ art: 'warten', zahlung: z, abgebrochen: false }),
    [],
  );
  const zurueck = useCallback(() => setPhase({ art: 'bereit' }), []);

  return { phase, starte, abbrechen, erneutNachfragen, zurueck };
}
